import React, { useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, Card } from "@mui/material";
import AOS from "aos";
import "aos/dist/aos.css";
import passport from "../../assets/images/Travel/passport.gif";
import embassy from "../../assets/images/Travel/consulate.gif";
import legal from "../../assets/images/Travel/contract.gif";
import interpreter from "../../assets/images/Travel/translator.gif";
import luggage from "../../assets/images/Travel/luggage.gif";
import lostPassport from "../../assets/images/Travel/nomad-visa.gif";

// ✅ Card data
const serviceCardsData = [
  {
    icon: embassy,
    title: "Embassy Support",
    description:
      "We help you reach the right embassy and navigate procedures during emergencies",
  },
  {
    icon: lostPassport,
    title: "Visa Expired Assistance",
    description:
      "Stuck abroad with an expired visa? We help arrange timely extensions per local regulations.",
  },
  {
    icon: legal,
    title: "Legal Referral Services",
    description:
      "Connect with trusted legal help in case of travel-related legal trouble overseas.",
  },
  {
    icon: interpreter,
    title: "Interpreter Referral Services",
    description:
      "Bridge the language gap—get interpreter assistance for better clarity in foreign lands.",
  },
  {
    icon: luggage,
    title: "Luggage Lost Assistance",
    description:
      "We coordinate with airlines and local partners to help track, report, and recover your missing baggage.",
  },
  {
    icon: passport,
    title: "Passport Lost Assistance",
    description:
      "Get quick guidance, embassy help, and support with documentation to replace lost passports efficiently.",
  },
];

// Modified to accept hover flag
function useTilt(ref, isHovered) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    function handleMouseMove(e) {
      if (!isHovered) return;
      const rect = node.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const px = (x / rect.width - 0.5) * 2;
      const py = (y / rect.height - 0.5) * 2;
      const rotateX = -py * 12;
      const rotateY = px * 12;
      node.style.transform = `perspective(700px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.03)`;
      node.style.zIndex = 2;
      node.style.boxShadow = `0 16px 36px 0 #dfc4b6bb, 0 2px 12px #e5dedacc`;
    }
    function handleMouseLeave() {
      node.style.transform =
        "perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)";
      node.style.zIndex = 1;
      node.style.boxShadow = "0 0px 0px 0 transparent";
    }
    node.addEventListener("mousemove", handleMouseMove);
    node.addEventListener("mouseleave", handleMouseLeave);

    // Clean up on unmount
    return () => {
      node.removeEventListener("mousemove", handleMouseMove);
      node.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [ref, isHovered]);
}

function TiltCard({ card, aosFade, aosDelay }) {
  const tiltRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useTilt(tiltRef, isHovered);

  // Reset box-shadow and transform when not hovered
  useEffect(() => {
    const node = tiltRef.current;
    if (!node) return;
    if (!isHovered) {
      // Set only border, NO boxShadow, and no tilt effect by default
      node.style.boxShadow = "none";
      node.style.transform = "perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)";
      node.style.zIndex = 1;
    }
  }, [isHovered]);

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      data-aos={aosFade}
      data-aos-delay={aosDelay}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Card
        ref={tiltRef}
        elevation={0}
        sx={{
          flexGrow: 1,
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          alignItems: "center",
          width: "22rem",
          minHeight: "205px",
          height: "100%",
          padding: "18px",
          border: "1px solid #dfc4b6ff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          // REMOVE default boxShadow and hover effect via MUI (keep plain)
          boxShadow: "none !important",
          transition:
            "transform 0.35s cubic-bezier(.2,1,.2,1), box-shadow 0.35s cubic-bezier(.2,1,.2,1)",
          willChange: "transform",
          cursor: "pointer",
          userSelect: "none",
          outline: "none",
        }}
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Card Icon as Image */}
        <img
          className="gif-color-change"
          src={card.icon}
          alt={card.title + " icon"}
          style={{
            width: 56,
            height: 56,
            marginBottom: 8,
            display: "block",
            objectFit: "contain",
          }}
        />

        {/* Title */}
        <Typography
          variant="h6"
          fontWeight="boldd"
          gutterBottom
          sx={{
            color: "#000000",
            paddingRight: "0px",
            fontSize: {
              sx: "1rem",
              md: "1.4rem",
            },
            lineHeight: 1.32,
          }}
        >
          {card.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#444",
            lineHeight: 1.6,
            mt: "auto",
            textAlign: "center",
          }}
        >
          {card.description}
        </Typography>
      </Card>
    </Grid>
  );
}

const EssentialServices = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <Container sx={{ py: 2 }}>
      <Typography variant="h4" align="center" fontWeight="boldd" mb={5}>
        Essential Services, Wherever you are.
      </Typography>

      <Grid
        container
        spacing={4}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignContent: "center",
          textAlign: "center",
        }}
      >
        {serviceCardsData.map((card, index) => (
          <TiltCard
            card={card}
            key={index}
            aosFade="fade-up"
            aosDelay={index * 100}
          />
        ))}
      </Grid>
    </Container>
  );
};

export default EssentialServices;
